import { useState } from "react";
import { Spinner } from "flowbite-react";
import { ArrowUpRight } from "lucide-react";
import yeatiLogo from "@/assets/images/yeati-logo.png";
import { LoginHelpDialog } from "@/features/auth/components/LoginLayout/LoginHelpDialog";
import { HELP_COPY } from "@/features/auth/config/loginHelp.config";
import type { OrganizationByTokenResponse } from "@/features/auth/services/organizationByToken.service";

type LoginHeaderProps = {
  orgBranding: OrganizationByTokenResponse | undefined
  organizationName: string | null
  isResolvingBranding: boolean
  isBetaEnabled: boolean
};

const ORGANIZATION_LABEL = "Votre espace";
const DEFAULT_SPACE_LABEL = "Espace salarié";

// Brand header: the CSE logo when a token provides it, the Yeati logo
// otherwise. While the branding resolves, a spinner avoids the Yeati logo
// flashing before the switch to the CSE.
const HeaderLogo = ({ orgBranding, isResolvingBranding }: Pick<LoginHeaderProps, "orgBranding" | "isResolvingBranding">) => {
  if (isResolvingBranding) {
    return <div className="flex h-12 w-28 items-center justify-center" aria-hidden><Spinner size="sm" /></div>;
  }
  if (orgBranding?.logo_url) {
    return <img src={orgBranding.logo_url} alt={orgBranding.name ?? "CSE"} className="max-h-12 max-w-[180px] object-contain" />;
  }
  return <img src={yeatiLogo} alt="Yeati" width="107" height="32" fetchPriority="high" />;
};

export const LoginHeader = ({
  orgBranding, organizationName, isResolvingBranding, isBetaEnabled,
}: LoginHeaderProps) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const spaceName = organizationName ?? orgBranding?.name ?? null;

  return (
    <header className="mx-auto flex w-full max-w-6xl items-center gap-4 px-4 py-6 sm:gap-6 sm:px-6">
      <HeaderLogo orgBranding={orgBranding} isResolvingBranding={isResolvingBranding} />
      {isBetaEnabled && <span className="rounded-full bg-amber-400 px-3 py-0.5 text-xs font-bold text-white">BETA</span>}
      {!isResolvingBranding && (
        <span className="hidden border-l border-gray-200 pl-4 text-xs leading-5 text-gray-500 sm:block dark:border-gray-700 dark:text-gray-400">
          {spaceName ? ORGANIZATION_LABEL : DEFAULT_SPACE_LABEL}
          {spaceName && <strong className="block text-sm text-gray-800 dark:text-gray-100">{spaceName}</strong>}
        </span>
      )}
      <button
        type="button"
        onClick={() => setIsHelpOpen(true)}
        className="ml-auto inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
        data-testid="login-help-button"
      >
        {HELP_COPY.BUTTON}
        <ArrowUpRight size={16} aria-hidden />
      </button>
      <LoginHelpDialog isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
    </header>
  );
};

LoginHeader.displayName = "LoginHeader";
